import React, { useState, useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import { getYears } from '../../utils/constCodeUtils';
import moment from 'moment';
import * as xlsx from 'xlsx';
import { prjDeptHeaders } from '../../utils/forecastStaticData';
import * as staticsApi from '../../lib/api/project';
import ProjectDeptStateTable from '../../components/project_dept_stat/ProjectDeptStateTable';
import AskDialog from '../../components/common/dialog/AskDialog';

const arrYears = getYears(3, 0);

const ProjectDeptStatContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const tableRef = useRef();
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [startMonth, setStartMonth] = useState('01');
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState('12');

  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState('');
  const onCancle = () => {
    setVisible(false);
  };

  const onConfirm = () => {
    setVisible(false);
  };

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const handleDownExcel = () => {
    const el = tableRef;
    const name = '프로젝트 통계';
    htmlToExcel(el.current, name);
  };

  const htmlToExcel = (tableEl, name) => {
    const wb = xlsx.utils.table_to_book(tableEl, {
      sheet: 'sheet1',
      raw: true,
    });
    xlsx.writeFile(wb, name + '.xlsx');
  };

  const onLoadData = async () => {
    setLoading(true);

    const searchData = {
      startDt: startYear + '-' + startMonth,
      endDt: endYear + '-' + endMonth,
    };

    try {
      const res = await staticsApi.getProjectsDeptStaticsBySearchRange(
        searchData,
      );
      if (res.data.list) {
        setData(res.data.list);
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (startYear && startMonth && endYear && endMonth) {
      onLoadData();
    }
  }, []);

  const checkValid = () => {
    let valid = true;
    const startDate = moment(startYear + '-' + startMonth);
    const endDate = moment(endYear + '-' + endMonth);

    valid = moment(endDate).isAfter(startDate);
    return valid;
  };
  const searchHandler = () => {
    const valid = checkValid();
    if (!valid) {
      setMessage('잘못된 검색기간이 설정되었습니다. 검색기간을 확인하세요');
      setVisible(true);
      return;
    }

    onLoadData();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', my: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box
          id="search-daterange"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <Box id="search-startdate">
            <FormControl
              variant="standard"
              sx={{ m: 1, minWidth: 100 }}
              size="small"
            >
              <Select
                labelId="start-year-label"
                id="start-year"
                value={startYear}
                label="시작년도"
                onChange={(e) => setStartYear(e.target.value)}
              >
                {arrYears &&
                  arrYears.map((item, index) => (
                    <MenuItem key={index} value={item}>
                      {item}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl
              variant="standard"
              sx={{ m: 1, minWidth: 80 }}
              size="small"
            >
              <Select
                labelId="start-month-label"
                id="selected-month"
                value={startMonth}
                label="시작월"
                onChange={(e) => setStartMonth(e.target.value)}
              >
                {[
                  '01',
                  '02',
                  '03',
                  '04',
                  '05',
                  '06',
                  '07',
                  '08',
                  '09',
                  '10',
                  '11',
                  '12',
                ].map((month) => (
                  <MenuItem key={month} value={month}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          ~
          <Box id="search-enddate">
            <FormControl
              variant="standard"
              sx={{ m: 1, minWidth: 100 }}
              size="small"
            >
              <Select
                labelId="end-year-label"
                id="end-year"
                value={endYear}
                label="종료년도"
                onChange={(e) => setEndYear(e.target.value)}
              >
                {arrYears &&
                  arrYears.map((item, index) => (
                    <MenuItem key={index} value={item}>
                      {item}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl
              variant="standard"
              sx={{ m: 1, minWidth: 80 }}
              size="small"
            >
              <Select
                labelId="end-month-label"
                id="end-month"
                value={endMonth}
                label="종료월"
                onChange={(e) => setEndMonth(e.target.value)}
              >
                {[
                  '01',
                  '02',
                  '03',
                  '04',
                  '05',
                  '06',
                  '07',
                  '08',
                  '09',
                  '10',
                  '11',
                  '12',
                ].map((month) => (
                  <MenuItem key={month} value={month}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <Box sx={{ mx: 1 }}>
            <Button variant="contained" color="info" onClick={searchHandler}>
              검색
            </Button>
          </Box>
          <Box>
            <Button
              variant="contained"
              color="success"
              onClick={handleDownExcel}
            >
              엑셀 다운로드
            </Button>
          </Box>
        </Box>
      </Box>
      <Divider sx={{ my: 1 }} />

      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        {loading ? (
          <Box sx={{ display: 'flex' }}>
            <CircularProgress />
          </Box>
        ) : Array.isArray(data) && data.length === 0 ? (
          <Box>
            <Typography variant="subtitle2">데이터가 없습니다.</Typography>
          </Box>
        ) : (
          <ProjectDeptStateTable
            headerRows={prjDeptHeaders}
            tableRef={tableRef}
            data={data}
          />
        )}
      </Box>
      <AskDialog
        visible={visible}
        title="검색 오류"
        description={message}
        confirmText="확인"
        onConfirm={onConfirm}
        onCancle={onCancle}
      />
    </Box>
  );
};
export default ProjectDeptStatContainer;
